<template>
  <div class="min-h-screen bg-gradient-to-br from-[#10103c] via-[#1a1a5c] to-[#2a2a7c] flex items-start justify-center px-4 pt-32 pb-8">
    <!-- Loading State -->
    <div v-if="loading" class="max-w-2xl w-full bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20">
      <div class="p-8 text-center">
        <div class="mx-auto w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mb-6">
          <svg class="w-10 h-10 text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
        </div>
        <h1 class="text-2xl font-bold text-white mb-2">Loading your status...</h1>
        <p class="text-gray-300">Please wait while we fetch your application details</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="max-w-2xl w-full bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20">
      <div class="p-8 text-center">
        <div class="mx-auto w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
          <svg class="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h1 class="text-2xl font-bold text-white mb-2">Unable to Load Status</h1>
        <p class="text-gray-300 mb-6">{{ error }}</p>
        <button
          @click="fetchUserStatus"
          class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200"
        >
          Try Again
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="max-w-2xl w-full bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20">
      <div class="p-8 text-center">
        <!-- Dynamic Header based on status -->
        <h1 class="text-4xl font-bold text-white mb-4">{{ statusTitle }}</h1>
        <p class="text-xl text-gray-300 mb-8">{{ statusSubtitle }}</p>

        <!-- Registration Summary -->
        <div class="bg-white/5 rounded-xl p-6 mb-8 text-left">
          <h2 class="text-lg font-semibold text-white mb-4 flex items-center">
            <svg class="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Registration Summary
          </h2>
          <div class="space-y-3 text-gray-300">
            <div class="flex justify-between">
              <span>Profile:</span>
              <span class="text-green-400">✓ Completed</span>
            </div>
            <div class="flex justify-between">
              <span>Creator Onboarding:</span>
              <span class="text-green-400">✓ Completed</span>
            </div>
            <div class="flex justify-between">
              <span>Application Status:</span>
              <span :class="statusBadgeClass">{{ statusText }}</span>
            </div>
          </div>
        </div>

        <!-- Dynamic Content based on status -->
        <!-- Accepted Status -->
        <div v-if="userStatus === 'accepted'" class="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-8 text-left">
          <h2 class="text-lg font-semibold text-white mb-4 flex items-center">
            <svg class="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            🎉 Congratulations! You're Approved!
          </h2>
          <div class="space-y-4 text-gray-300">
            <div class="flex items-start">
              <span class="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">✓</span>
              <div>
                <p class="text-white font-medium">Welcome to Rendery Consortium</p>
                <p class="text-sm">Your application has been approved and you're now part of our creator community</p>
              </div>
            </div>
            <div class="flex items-start">
              <span class="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
              <div>
                <p class="text-white font-medium">Await for videos to process</p>
                <p class="text-sm">Please keep an eye on your email, we might need you to upload some of them.</p>
              </div>
            </div>
            <div class="flex items-start">
              <span class="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
              <div>
                <p class="text-white font-medium">Join the Community</p>
                <p class="text-sm">Connect with other creators and participate in consortium governance, will notify you when our forum is live.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Rejected Status -->
        <div v-else-if="userStatus === 'rejected'" class="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8 text-left">
          <h2 class="text-lg font-semibold text-white mb-4 flex items-center">
            <svg class="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Application Status Update
          </h2>
          <div class="space-y-4 text-gray-300">
            <div class="flex items-start">
              <span class="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">!</span>
              <div>
                <p class="text-white font-medium">Application Not Approved</p>
                <p class="text-sm">Unfortunately, your application doesn't meet our current criteria</p>
              </div>
            </div>
            <div class="flex items-start">
              <span class="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
              <div>
                <p class="text-white font-medium">Review Requirements</p>
                <p class="text-sm">Check our updated creator guidelines and requirements</p>
              </div>
            </div>
            <div class="flex items-start">
              <span class="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
              <div>
                <p class="text-white font-medium">Reapply Later</p>
                <p class="text-sm">You may reapply once you meet the updated requirements</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Status (Default) -->
        <div v-else class="bg-white/5 rounded-xl p-6 mb-8 text-left">
          <h2 class="text-lg font-semibold text-white mb-4 flex items-center">
            <svg class="w-5 h-5 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            What's Next?
          </h2>
          <div class="space-y-4 text-gray-300">
            <div class="flex items-start">
              <span class="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
              <div>
                <p class="text-white font-medium">Review Process</p>
                <p class="text-sm">Our team will review your application and channel details</p>
              </div>
            </div>
            <div class="flex items-start">
              <span class="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
              <div>
                <p class="text-white font-medium">Status Update</p>
                <p class="text-sm">You'll receive an update on your application status within 2-3 business days</p>
              </div>
            </div>
            <div class="flex items-start">
              <span class="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
              <div>
                <p class="text-white font-medium">Start Earning</p>
                <p class="text-sm">Once approved, you can start listing your videos and earning from the marketplace</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-center space-x-4">
          <button
            @click="goToHome"
            class="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105"
          >
            Return to Home
          </button>
          <button
            @click="fetchUserStatus"
            class="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-200 border border-white/20"
          >
            Refresh Status
          </button>
        </div>

        <!-- Contact Info -->
        <div class="mt-8 pt-6 border-t border-white/10">
          <p class="text-gray-400 text-sm mb-2">Questions about your application?</p>
          <p class="text-gray-300 text-sm">
            Contact us at 
            <a href="mailto:nancy@rendery.org" class="text-blue-400 hover:text-blue-300 transition-colors">
              nancy@rendery.org
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiCall } from '../config/api.js'

export default {
  name: 'RegistrationComplete',
  data() {
    return {
      userEmail: '',
      userStatus: 'pending', // 'pending', 'accepted', 'rejected'
      loading: true,
      error: null,
      user: null
    };
  },
  computed: {
    statusTitle() {
      switch (this.userStatus) {
        case 'accepted':
          return 'Thanks for applying to Rendery!';
        case 'rejected':
          return 'Application Update';
        default:
          return 'Thanks for applying to Rendery!';
      }
    },
    statusSubtitle() {
      switch (this.userStatus) {
        case 'accepted':
          return 'Your application has been approved! 🎉';
        case 'rejected':
          return 'Thank you for your interest in joining our community';
        default:
          return 'Your registration has been completed successfully';
      }
    },
    statusText() {
      switch (this.userStatus) {
        case 'accepted':
          return '✓ Approved';
        case 'rejected':
          return '✗ Not Approved';
        default:
          return '⏳ Under Review';
      }
    },
    statusBadgeClass() {
      switch (this.userStatus) {
        case 'accepted':
          return 'text-green-400';
        case 'rejected':
          return 'text-red-400';
        default:
          return 'text-yellow-400';
      }
    },
    statusIconClass() {
      switch (this.userStatus) {
        case 'accepted':
          return 'bg-green-500/20';
        case 'rejected':
          return 'bg-red-500/20';
        default:
          return 'bg-yellow-500/20';
      }
    },
    statusIconColor() {
      switch (this.userStatus) {
        case 'accepted':
          return 'text-green-400';
        case 'rejected':
          return 'text-red-400';
        default:
          return 'text-yellow-400';
      }
    }
  },
  async mounted() {
    await this.fetchUserStatus();
    
    // Clean up any temporary storage
    localStorage.removeItem('tempUserEmail');
    localStorage.removeItem('tempUserId');
    localStorage.removeItem('completedUserId');
  },
  methods: {
    async fetchUserStatus() {
      this.loading = true;
      this.error = null;
      
      try {
        // Get token from auth state
        const token = this.$authState?.token || localStorage.getItem('auth_token');
        
        if (!token) {
          throw new Error('No authentication token found. Please sign in again.');
        }

        // Fetch user profile from backend
        const { response, data } = await apiCall('auth/profile', {
          method: 'GET',
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Your session has expired. Please sign in again.');
          }
          throw new Error(`Failed to fetch profile: ${response.status}`);
        }
        
        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch user profile');
        }

        this.user = data.user;
        this.userEmail = data.user.email;
        
        // Determine user status based on profile data
        if (!data.user.profileCompleted || !data.user.additionalQuestionsData) {
          // User hasn't completed onboarding - shouldn't normally reach this page
          this.userStatus = 'incomplete';
        } else if (data.user.accepted === true) {
          this.userStatus = 'accepted';
        } else if (data.user.rejectedAt) {
          // User has been explicitly rejected (has rejection timestamp)
          this.userStatus = 'rejected';
        } else {
          // User has completed onboarding but not yet reviewed
          this.userStatus = 'pending';
        }

      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching user status:', error);
        }
        this.error = error.message;
        
        // Fallback to localStorage for basic info
        this.userEmail = localStorage.getItem('tempUserEmail') || 'your-email@example.com';
      } finally {
        this.loading = false;
      }
    },
    goToHome() {
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 